import {useTranslation} from 'react-i18next';
import React, {Fragment, useEffect, useState} from 'react';
import Grid from '@mui/material/Grid';
import makeStyles from '@mui/styles/makeStyles';
import _ from 'lodash';
import moment from 'moment';
import {useSelector} from 'react-redux';
import {user} from '../../../../store/reducers/user';
import MessageBox from '../../../../util/messageBox';
import API from '../../../../app/api';
import {consultantDictionary, ControlConsultantComponent} from '../../../../controls/agyle/clinitian';
import Notes from '../../../../util/notes';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import AgyleBlockSwitch from '../../../../components/ui/switch/agyleBlockSwitch';
import Typography from '@mui/material/Typography';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import useOpenState from '../../../../hooks/useOpenState';
import MiniFormDialog from '../../../../components/modal/miniFormDialog';

const useStyles = makeStyles((theme) => ({
    background: {
        backgroundColor: theme.palette.background.paperDarker,
    },
    dialogTitle: {
        textAlign: 'center',
        padding: '10px',
    },
    dialogContent: {
        height: 'auto',
    },
    button: {
        background: '#fff',
        color: '#000',
        width: '10%',
        '&:hover': {
            background: '#fff',
        },
    },
    error: {
        color: '#f44336',
    },
    input: {
        width: '100%',
        fontSize: '15px',
        marginBottom: 10,
        borderRadius: '6%',
    },
    switchContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 2,
    },
}));

const NurseForm = (props) => {
    const {t} = useTranslation();
    const classesLocal = useStyles();
    const userObject = useSelector(user);

    const [form, setForm] = useState({
        who: null,
        when: moment(),
        mrsa: false,
        property: false,
        description: '',
    });

    const [isConfirmOpen, openConfirm, closeConfirm] = useOpenState(false, true);
    const [errorPicker, setErrorPicker] = useState(null);
    const [formIsModified, setFormIsModified] = React.useState(false);

    useEffect(() => {
        if (_.isUndefined(form?.who) || _.isEmpty(form?.who)) {
            //TODO: have to change to consultants structute!!!!
            API.getDictByURL(`/$dict/Nurses`).then(dictData => {
                if (dictData) {
                    for (let nurse of dictData.__data) {
                        if (nurse.userName === userObject.userName) {
                            setForm(prevState => ({
                                ...prevState,
                                who: nurse,
                            }));
                            setFormIsModified(true);
                            break;
                        }
                    }
                }
            });
        }

        if (props.patient?.studies?.edau?.nurseForm) {
            const data = {
                ...props.patient?.studies?.edau?.nurseForm,
            };
            setForm(data);
        }
    }, []);

    const prepareNote = () => {
        return Notes.getNurseFormNote(form);
    };

    const validation = () => {
        return (form.who && form.when && !errorPicker) || (!form.who && !form.when);
    };

    const submit = () => {
        if (validation() && !errorPicker) {
            let data = _.cloneDeep(form);
            const objToSave = ([
                {
                    name: 'studies.edau.nurseForm',
                    value: data,
                    noteToAdd: prepareNote(),
                },
            ]);

            props.onChange(objToSave);
            props.onClose();
        }
    };

    const clearData = () => {
        setForm({
            who: null,
            when: null,
            mrsa: false,
            property: false,
            description: '',
        });
        closeConfirm();
    };

    return (
        <>
            <MiniFormDialog
                title={t(props._formTitle)}
                open={props.open}
                onClose={props.onClose}
                disableSubmit={!formIsModified}
                onSave={submit}
                formOptions={props._formOptions}
                buttons={[
                    <AgyleButton
                        color="tertiary"
                        onClick={openConfirm}
                        key="cancel"
                    >
                        {t('Cancel')}
                    </AgyleButton>,
                ]}
            >
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <ControlConsultantComponent
                            typeDict={'Nurses'}
                            agyleRefresh
                            label={t('Nurse')}
                            allowedConsultants={consultantDictionary.nurseForm}
                            className={classesLocal.input}
                            onChange={event => {
                                setForm(
                                    prevState => ({
                                        ...prevState,
                                        who: event,
                                    }),
                                );
                                setFormIsModified(true);
                            }}
                            value={form.who}
                            variant="outlined"
                            refresh
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <AgyleDatePicker
                            label={t('Date and Time')}
                            type="dateTime"
                            value={form.when}
                            minDateTime={moment(props.patient?.admission?.startDate)}
                            maxDateTime={moment()}
                            onError={setErrorPicker}
                            onChange={event => {
                                setForm(prevState => {
                                    setFormIsModified(true);
                                    return ({...prevState, when: moment(event).toISOString()});
                                });
                            }}
                            noBorder={false}
                            withNewBackground
                            size="large"
                        />
                    </Grid>

                    <Grid item xs={12} sx={{pl: 1}} className={classesLocal.switchContainer}>
                        <AgyleBlockSwitch
                            onChange={event => setForm(prevState => {
                                    setFormIsModified(true);
                                    return ({
                                        ...prevState,
                                        mrsa: event,
                                    });
                                },
                            )}
                            value={form.mrsa}
                        />

                        <Typography variant="body1" sx={{pl: 1}}>
                            {t('MRSA Screen Completed')}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sx={{pl: 1}} className={classesLocal.switchContainer}>
                        <AgyleBlockSwitch
                            onChange={event => setForm(prevState => {
                                    setFormIsModified(true);
                                    return ({
                                        ...prevState,
                                        property: event,
                                    });
                                },
                            )}
                            value={form.property}
                        />

                        <Typography variant="body1" sx={{pl: 1}}>
                            {t('Property Form Completed')}
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <AgyleTextField
                            label={t('Description')}
                            className={classesLocal.input}
                            onChange={event => setForm(
                                prevState => {
                                    setFormIsModified(true);
                                    return ({
                                        ...prevState,
                                        description: event.target.value,
                                    });
                                },
                            )}
                            fullWidth
                            multiline
                            rows={4}
                            value={form.description}
                            noBorder={false}
                            withNewBackground
                            size="large"
                        />
                    </Grid>

                </Grid>
            </MiniFormDialog>

            <MessageBox
                onClose={closeConfirm}
                title={t('Confirmation')}
                message={t('Are you sure you want to clear entry?')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={clearData}
                open={isConfirmOpen}
            />
        </>
    );
};

export default NurseForm;
